import { currentTranslation, fmt } from '../i18n'

const cache = new Map<
  string,
  { dateTime: Intl.DateTimeFormat; relative: Intl.RelativeTimeFormat }
>()

/** Date formatters in the admin language (ADR-043); plain `en` uses British dates. */
function formatters() {
  const { locale } = currentTranslation()
  let f = cache.get(locale)

  if (!f) {
    const tag = locale === 'en' ? 'en-GB' : locale

    f = {
      dateTime: new Intl.DateTimeFormat(tag, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
      relative: new Intl.RelativeTimeFormat(tag, { numeric: 'auto' }),
    }
    cache.set(locale, f)
  }

  return f
}

export function formatDateTime(iso: string | null | undefined): string {
  return iso ? formatters().dateTime.format(new Date(iso)) : '—'
}

export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '—'
  const diff = (new Date(iso).getTime() - Date.now()) / 1000
  const abs = Math.abs(diff)
  const { dateTime, relative } = formatters()

  if (abs < 60) return relative.format(Math.round(diff), 'second')
  if (abs < 3600) return relative.format(Math.round(diff / 60), 'minute')
  if (abs < 86400) return relative.format(Math.round(diff / 3600), 'hour')
  if (abs < 30 * 86400) return relative.format(Math.round(diff / 86400), 'day')

  return dateTime.format(new Date(iso))
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const units = ['KB', 'MB', 'GB', 'TB']
  let v = bytes / 1024
  let i = 0

  while (v >= 1024 && i < units.length - 1) {
    v /= 1024
    i++
  }

  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`
}

export function humanize(key: string): string {
  return key
    .replace(/[_.-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^\w/, (c) => c.toUpperCase())
}

/** Display name for an actor id (user ids are shortened; services/plugins are labelled). */
export function actorLabel(
  actor: string | null | undefined,
  me?: { id: string; displayName: string } | null,
): string {
  const { t } = currentTranslation()

  if (!actor) return t.actor.system
  if (me && actor === me.id) return t.actor.you
  if (actor.startsWith('service:'))
    return fmt(t.actor.service, { name: actor.slice(8) })
  if (actor.startsWith('plugin:'))
    return fmt(t.actor.plugin, { name: actor.slice(7) })

  return fmt(t.actor.user, { id: actor.slice(0, 8) })
}

export function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')

  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}
